import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button,
  Tabs, TabList, TabPanels, Tab, TabPanel, VStack, HStack, Box, Text, Input, Select,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, useToast, Badge, Spinner, SimpleGrid,
  Textarea, NumberInput, NumberInputField, Icon, Link, Stat, StatLabel, StatNumber, Divider,
} from '@chakra-ui/react';
import { FiTrash2, FiUpload, FiPlus, FiFile } from 'react-icons/fi';
import deportesApi, { Articulo, Movimiento, ArchivoGenerico } from '../../../api/deportesApi';
import { ROJO, ROJO_HOVER, fmtFecha, API_UPLOADS } from '../_shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  articulo: Articulo | null;
  onCambio?: () => void;
}

export const TIPOS_MOVIMIENTO = ['Alta', 'Entrada', 'Salida', 'Prestamo', 'Devolucion', 'Mantenimiento', 'Baja'];
const TIPO_COLOR: Record<string, string> = {
  Alta: 'green', Entrada: 'teal', Salida: 'orange', Prestamo: 'yellow',
  Devolucion: 'blue', Mantenimiento: 'purple', Baja: 'red',
};

const ModalMovimientos: React.FC<Props> = ({ isOpen, onClose, articulo, onCambio }) => {
  const toast = useToast();
  const fotoRef = useRef<HTMLInputElement>(null);

  const [art, setArt] = useState<Articulo | null>(articulo);
  const [movs, setMovs] = useState<Movimiento[]>([]);
  const [fotos, setFotos] = useState<ArchivoGenerico[]>([]);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [nv, setNv] = useState<any>({ tipo: 'Entrada', cantidad: 1, responsable: '', observaciones: '' });

  const cargar = async () => {
    if (!articulo) return;
    setCargando(true);
    try {
      const [a, m, fo] = await Promise.all([
        deportesApi.obtenerArticulo(articulo.id).catch(() => articulo),
        deportesApi.listarMovimientos(articulo.id),
        deportesApi.listarArticuloFotos(articulo.id),
      ]);
      setArt(a);
      setMovs(m || []);
      setFotos(fo || []);
    } finally { setCargando(false); }
  };

  useEffect(() => { if (isOpen) cargar(); /* eslint-disable-next-line */ }, [isOpen, articulo]);

  const registrar = async () => {
    if (!articulo) return;
    if (!nv.tipo || !nv.cantidad) { toast({ title: 'Tipo y cantidad requeridos', status: 'warning', duration: 2500 }); return; }
    setGuardando(true);
    try {
      await deportesApi.crearMovimiento(articulo.id, {
        tipo: nv.tipo, cantidad: Number(nv.cantidad),
        responsable: nv.responsable || null, observaciones: nv.observaciones || null,
      });
      toast({ title: 'Movimiento registrado', status: 'success', duration: 2000 });
      setNv({ tipo: 'Entrada', cantidad: 1, responsable: '', observaciones: '' });
      cargar();
      onCambio?.();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo registrar', status: 'error', duration: 4000 });
    } finally { setGuardando(false); }
  };

  const subirFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !articulo) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      await deportesApi.subirArticuloFoto(articulo.id, fd);
      toast({ title: 'Foto subida', status: 'success', duration: 2000 });
      cargar();
    } catch (err: any) { toast({ title: 'Error', description: err?.response?.data?.message, status: 'error', duration: 3000 }); }
    finally { setSubiendo(false); if (fotoRef.current) fotoRef.current.value = ''; }
  };
  const delFoto = async (id: number) => {
    try { await deportesApi.eliminarArticuloFoto(id); cargar(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  const esImagen = (a: ArchivoGenerico) => (a.mimeType || '').startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes((a.tipoArchivo || '').toLowerCase());

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>
          {articulo?.nombre} <Text as="span" fontSize="sm" color="gray.500">({articulo?.numeroInventario})</Text>
        </ModalHeader>
        <ModalBody py={4}>
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={3}>
            <Stat><StatLabel>Cantidad total</StatLabel><StatNumber>{art?.cantidad ?? 0}</StatNumber></Stat>
            <Stat><StatLabel>Disponible</StatLabel><StatNumber color={ROJO}>{art?.cantidadDisponible ?? 0}</StatNumber></Stat>
            <Stat><StatLabel>Estado</StatLabel><StatNumber fontSize="lg"><Badge>{art?.estadoFisico}</Badge></StatNumber></Stat>
          </SimpleGrid>
          <Divider mb={3} />

          {cargando ? (
            <VStack py={8}><Spinner color={ROJO} /></VStack>
          ) : (
            <Tabs colorScheme="red" variant="enclosed">
              <TabList>
                <Tab>Movimientos ({movs.length})</Tab>
                <Tab>Fotos ({fotos.length})</Tab>
              </TabList>
              <TabPanels>
                {/* MOVIMIENTOS */}
                <TabPanel px={0}>
                  <Box p={3} bg="gray.50" borderRadius="md" mb={3}>
                    <Text fontSize="xs" fontWeight="bold" color={ROJO} mb={2}>NUEVO MOVIMIENTO</Text>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2}>
                      <Select size="sm" value={nv.tipo} onChange={(e) => setNv({ ...nv, tipo: e.target.value })}>
                        {TIPOS_MOVIMIENTO.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Select>
                      <NumberInput size="sm" min={1} value={nv.cantidad} onChange={(v) => setNv({ ...nv, cantidad: v })}><NumberInputField placeholder="Cantidad" /></NumberInput>
                      <Input size="sm" placeholder="Responsable" value={nv.responsable} onChange={(e) => setNv({ ...nv, responsable: e.target.value })} />
                      <Button size="sm" leftIcon={<FiPlus />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={registrar} isLoading={guardando}>Registrar</Button>
                    </SimpleGrid>
                    <Textarea size="sm" mt={2} rows={2} placeholder="Observaciones" value={nv.observaciones} onChange={(e) => setNv({ ...nv, observaciones: e.target.value })} />
                    <Text fontSize="2xs" color="gray.500" mt={1}>Salida, Préstamo y Baja no pueden exceder la cantidad disponible.</Text>
                  </Box>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg={ROJO}>
                        <Tr>
                          <Th color="white">Fecha</Th><Th color="white">Tipo</Th><Th color="white" isNumeric>Cant.</Th>
                          <Th color="white" isNumeric>Previa</Th><Th color="white" isNumeric>Nueva</Th>
                          <Th color="white">Responsable</Th><Th color="white">Observaciones</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {movs.length === 0 ? (
                          <Tr><Td colSpan={7} textAlign="center" py={6}><Text color="gray.400">Sin movimientos</Text></Td></Tr>
                        ) : movs.map((m) => (
                          <Tr key={m.id}>
                            <Td fontSize="xs">{fmtFecha(m.fecha)}</Td>
                            <Td><Badge colorScheme={TIPO_COLOR[m.tipo] || 'gray'}>{m.tipo}</Badge></Td>
                            <Td isNumeric>{m.cantidad}</Td>
                            <Td isNumeric>{m.existenciaPrevia ?? '—'}</Td>
                            <Td isNumeric>{m.existenciaNueva ?? '—'}</Td>
                            <Td fontSize="xs">{m.responsable || '—'}</Td>
                            <Td fontSize="xs" maxW="180px"><Text noOfLines={2}>{m.observaciones || '—'}</Text></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* FOTOS */}
                <TabPanel px={0}>
                  <input ref={fotoRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={subirFoto} />
                  <Button size="sm" leftIcon={<FiUpload />} colorScheme="red" variant="outline" mb={3} onClick={() => fotoRef.current?.click()} isLoading={subiendo}>Subir foto</Button>
                  {fotos.length === 0 ? (
                    <Box py={6} textAlign="center"><Text color="gray.400">Sin fotos</Text></Box>
                  ) : (
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                      {fotos.map((a) => (
                        <Box key={a.id} borderWidth="1px" borderRadius="md" p={2} position="relative">
                          <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" colorScheme="red" position="absolute" top={1} right={1} onClick={() => delFoto(a.id)} />
                          {esImagen(a) ? (
                            <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal>
                              <img src={`${API_UPLOADS}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4 }} />
                            </Link>
                          ) : (<VStack py={4}><Icon as={FiFile} boxSize={7} color="red.500" /></VStack>)}
                          <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal fontSize="2xs" color="blue.500" noOfLines={1}>{a.nombreArchivo}</Link>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalMovimientos;
